(() => {
  const FAVORITES_KEY = 'kk_favorites';

  const readFavorites = () => {
    try {
      const value = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && item.handle) : [];
    } catch (_) {
      return [];
    }
  };

  const writeFavorites = (items) => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(items));
      window.dispatchEvent(new CustomEvent('kk:favorites-changed', { detail: { items } }));
    } catch (_) {}
  };

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('sl-SI', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount).replace(/\s*EUR$/, ' €');
  };

  const escapeHtml = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const productImage = (product, fallback) => {
    if (product.featured_image) return product.featured_image;
    if (Array.isArray(product.images) && product.images.length) return product.images[0];
    return fallback || '';
  };

  const buildCard = (product, savedItem) => {
    const article = document.createElement('article');
    article.className = 'kk-wishlist-card';
    article.dataset.productHandle = product.handle;

    const variant = Array.isArray(product.variants)
      ? product.variants.find((item) => item.available) || product.variants[0]
      : null;
    const productUrl = product.url || savedItem.url || `/products/${encodeURIComponent(product.handle)}`;
    const image = productImage(product, savedItem.image);
    const compareAt = Number(product.compare_at_price || variant?.compare_at_price || 0);
    const price = Number(product.price || variant?.price || 0);
    const isSale = compareAt > price;
    const canAdd = Boolean(product.available && variant?.id);
    const hasOnlyDefaultVariant = Array.isArray(product.options) && product.options.length === 1 && product.options[0] === 'Title';

    article.innerHTML = `
      <button class="kk-wishlist-card__remove" type="button" aria-label="Odstrani ${escapeHtml(product.title)} iz priljubljenih">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7.2-4.4-9.6-9.1C.2 7.6 2.6 3 7.1 3c2 0 3.7 1 4.9 2.5C13.2 4 14.9 3 16.9 3c4.5 0 6.9 4.6 4.7 8.9C19.2 16.6 12 21 12 21Z"/></svg>
      </button>
      <a class="kk-wishlist-card__media" href="${escapeHtml(productUrl)}">
        ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="500" height="500">` : ''}
      </a>
      <div class="kk-wishlist-card__content">
        <a class="kk-wishlist-card__title" href="${escapeHtml(productUrl)}">${escapeHtml(product.title)}</a>
        <div class="kk-wishlist-card__price">
          <span class="kk-wishlist-card__price-current${isSale ? ' kk-wishlist-card__price-current--sale' : ''}">${money(price)}</span>
          ${isSale ? `<span class="kk-wishlist-card__price-compare">${money(compareAt)}</span>` : ''}
        </div>
        ${canAdd && hasOnlyDefaultVariant
          ? `<button class="kk-wishlist-card__button" type="button" data-add-variant="${variant.id}">Dodaj v košarico</button>`
          : `<a class="kk-wishlist-card__button" href="${escapeHtml(productUrl)}">${product.available ? 'Izberi možnosti' : 'Razprodano'}</a>`}
      </div>`;

    return article;
  };

  const fetchProduct = async (item) => {
    const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Product ${item.handle} not found`);
    return response.json();
  };

  const initWishlist = async (root) => {
    if (root.dataset.kkWishlistReady === 'true') return;
    root.dataset.kkWishlistReady = 'true';

    const grid = root.querySelector('[data-kk-wishlist-grid]');
    const empty = root.querySelector('[data-kk-wishlist-empty]');
    const loading = root.querySelector('[data-kk-wishlist-loading]');
    const count = root.querySelector('[data-kk-wishlist-count]');

    const render = async () => {
      const favorites = readFavorites();
      grid.innerHTML = '';
      count.textContent = favorites.length === 1 ? '1 izdelek' : `${favorites.length} izdelkov`;

      if (!favorites.length) {
        loading.hidden = true;
        grid.hidden = true;
        empty.hidden = false;
        return;
      }

      loading.hidden = false;
      grid.hidden = true;
      empty.hidden = true;

      const results = await Promise.allSettled(favorites.map(async (item) => ({
        item,
        product: await fetchProduct(item),
      })));

      const validHandles = [];
      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        validHandles.push(result.value.item.handle);
        grid.appendChild(buildCard(result.value.product, result.value.item));
      });

      if (validHandles.length !== favorites.length) {
        writeFavorites(favorites.filter((item) => validHandles.includes(item.handle)));
      }

      loading.hidden = true;
      grid.hidden = grid.children.length === 0;
      empty.hidden = grid.children.length > 0;
      count.textContent = grid.children.length === 1 ? '1 izdelek' : `${grid.children.length} izdelkov`;
    };

    root.addEventListener('click', async (event) => {
      const removeButton = event.target.closest('.kk-wishlist-card__remove');
      if (removeButton) {
        const card = removeButton.closest('.kk-wishlist-card');
        const handle = card?.dataset.productHandle;
        if (!handle) return;
        writeFavorites(readFavorites().filter((item) => item.handle !== handle));
        card.remove();
        const remaining = grid.children.length;
        count.textContent = remaining === 1 ? '1 izdelek' : `${remaining} izdelkov`;
        if (!remaining) {
          grid.hidden = true;
          empty.hidden = false;
        }
        return;
      }

      const addButton = event.target.closest('[data-add-variant]');
      if (addButton) {
        addButton.disabled = true;
        const originalText = addButton.textContent;
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(addButton.dataset.addVariant), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('Add to cart failed');
          addButton.textContent = 'Dodano';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch (_) {
          addButton.textContent = 'Poskusi znova';
        } finally {
          window.setTimeout(() => {
            addButton.textContent = originalText;
            addButton.disabled = false;
          }, 1600);
        }
      }
    });

    await render();
  };

  const init = () => document.querySelectorAll('[data-kk-wishlist]').forEach(initWishlist);
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
